import { CountryFlag } from 'common/components/CountryFlag/CountryFlag';
import { QuerySuspenseBase } from 'common/components/QuerySuspenseBase/QuerySuspenseBase';
import { UnexpectedErrorCallout } from 'common/components/UnexpectedErrorCallout/UnexpectedErrorCallout';
import { useTranslation } from 'common/hooks/useTranslation';
import {
  getTripDates,
  getTripDestination,
  getTripDestinationCountryCode,
} from 'modules/requests/utils/trips';
import { TripSkeleton } from 'modules/trips/components/TripSkeleton/TripSkeleton';
import { routeFor, routes } from 'src/core/constants/routes';
import history from 'src/core/history';
import { useCompanyId } from 'src/core/modules/app/hooks/useCompanyId';
import { useCurrentTripsQuery } from 'src/core/modules/requests/hooks/useCurrentTripsQuery';

export const UpcomingTrips = () => {
  const { t, localeFormat } = useTranslation('global');
  const companyId = useCompanyId();
  const tripsQuery = useCurrentTripsQuery({
    isEnabled: true,
  });

  return (
    <div className="mt-40 px-[208px]">
      <h3 className="heading-xl mb-24">{t('travel.upcomingTrips')}</h3>
      <QuerySuspenseBase
        queryState={tripsQuery}
        loading={
          <div className="flex gap-16">
            <TripSkeleton />
            <TripSkeleton />
          </div>
        }
        fallback={() => <UnexpectedErrorCallout />}
      >
        {(response) => {
          if (!response.data.trips || response.data.trips.length === 0) {
            return (
              <p className="text-content-secondary-bg-primary">
                {t('travel.upcomingTripsEmptyState')}
              </p>
            );
          }
          return (
            <div className="flex flex-wrap gap-16">
              {response.data.trips.map((trip) => {
                const destination = getTripDestination(trip.route);
                const dates = getTripDates(
                  new Date(trip.startDate),
                  new Date(trip.endDate),
                  localeFormat,
                );
                const countryCode = getTripDestinationCountryCode(trip.route);

                const handleTripClick = () => {
                  history.push(
                    routeFor(routes.TRAVEL_TRIP_DETAILS.path, {
                      company: companyId,
                      id: trip.id,
                    }),
                  );
                };

                return (
                  <div
                    key={trip.id}
                    role="button"
                    tabIndex={0}
                    className="bg-background-primary-default rounded-12 border-border-default hover:border-border-hover focus:outline-border-focus flex w-[calc(50%-8px)] cursor-pointer border p-16 transition-colors focus:outline-2 focus:outline-offset-2"
                    onClick={handleTripClick}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        handleTripClick();
                      }
                    }}
                  >
                    <div>
                      <div className="title-l">{destination}</div>
                      <div className="text-content-secondary-bg-primary body-l">
                        {dates}
                      </div>
                    </div>
                    {countryCode && (
                      <div className="rounded-12 bg-background-secondary-brand-default ml-auto flex h-40 w-40 justify-center p-8">
                        <CountryFlag
                          country={countryCode}
                          className="w-[20px]"
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          );
        }}
      </QuerySuspenseBase>
    </div>
  );
};
